// Ярлык Mini App на главном экране телефона (HQ-772, решение владельца 22.10 В4 «б»): бесплатный канал возврата без
// бота и рекламы. Предлагаем один раз — на 3-й день визитов, если Telegram говорит missed; плюс строка в меню.
// Здесь — дни визитов, правило показа и статус ярлыка на сессию; лист — HomeScreenPrompt.tsx. Образец — крикет HQ-725.
import { useSyncExternalStore } from 'react';
import { HOME_SCREEN_PROMPT, homeScreenPrompt, type HomeScreenPromptSource } from '../analytics/events';
import { track } from '../analytics/track';
import { addToHomeScreen, canAddToHomeScreen, checkHomeScreenStatus, onHomeScreenAdded, type HomeScreenStatus } from '../telegram/webapp';

export const VISIT_DAYS_KEY = 'soccer.visit_days';
export const PROMPT_SHOWN_KEY = 'soccer.home_prompt.shown';
/** На какой день визитов (уникальные даты по местному времени) предлагаем ярлык. */
export const PROMPT_DAY = 3;
/** Сколько последних дней храним: правилу нужно лишь «не меньше трёх», список не растёт без предела. */
const DAYS_LIMIT = 30;

/** Дата по местному времени: «2026-10-22». */
export function localDay(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function visitDays(): string[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(VISIT_DAYS_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((d): d is string => typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) : [];
  } catch { return []; }
}

/** Отметить сегодняшний визит; возвращает число дней визитов вместе с сегодняшним. */
export function recordVisit(now: Date = new Date()): number {
  const days = [...new Set([...visitDays(), localDay(now)])].sort().slice(-DAYS_LIMIT);
  try { localStorage.setItem(VISIT_DAYS_KEY, JSON.stringify(days)); } catch { /* приватный режим — счётчик на сессию */ }
  return days.length;
}

export function promptShown(): boolean {
  try { return localStorage.getItem(PROMPT_SHOWN_KEY) !== null; } catch { return false; }
}
export function markPromptShown(now: Date = new Date()): void {
  try { localStorage.setItem(PROMPT_SHOWN_KEY, localDay(now)); } catch { /* приватный режим — лист не повторится в сессии */ }
}

/** Где человек: onTab — корень вкладки (не экран матча, не меню, не «Скоро»); fromLink — сессия открыта по ссылке
 *  (startapp): пришёл за конкретным экраном — лист в эту сессию не предлагаем. */
export interface PromptPlace { onTab: boolean; fromLink: boolean }

/** Правило автопоказа: ярлыка нет (missed), 3-й или поздний день визитов, лист ещё не показывали, человек на корне вкладки
 *  и открыл приложение не по ссылке. added / unsupported / unknown / статус ещё не пришёл — ничего. */
export function shouldPrompt(p: PromptPlace & { status: HomeScreenStatus | null; days: number; shown: boolean }): boolean {
  return p.status === 'missed' && p.days >= PROMPT_DAY && !p.shown && p.onTab && !p.fromLink;
}

// ---- Статус ярлыка: спрашиваем Telegram один раз за сессию, homeScreenAdded переводит в added.
let status: HomeScreenStatus | null = null;
let started = false;
const listeners = new Set<() => void>();
const set = (next: HomeScreenStatus) => {
  // added — окончательно: поздний ответ на проверку не возвращает строку меню в «Добавить»
  if (status === 'added' || status === next) return;
  status = next;
  listeners.forEach((cb) => cb());
};
function start(): void {
  if (started) return;
  started = true;
  if (!canAddToHomeScreen()) { status = 'unsupported'; return; }
  onHomeScreenAdded(() => set('added'));
  checkHomeScreenStatus(set);
}

/** Статус ярлыка; null — Telegram ещё не ответил. */
export function homeScreenStatus(): HomeScreenStatus | null {
  start();
  return status;
}
export function subscribeHomeScreen(cb: () => void): () => void {
  start();
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}
export function useHomeScreenStatus(): HomeScreenStatus | null {
  // SPA без серверного рендера: снимок «сервера» — тот же статус (renderToString в тестах видит настоящий)
  return useSyncExternalStore(subscribeHomeScreen, homeScreenStatus, homeScreenStatus);
}

/** Автопоказ: правило сошлось — лист отмечен показанным (больше не повторится) и ушло shown; true — открыть лист. */
export function claimPrompt(p: PromptPlace & { status: HomeScreenStatus | null; days: number }): boolean {
  if (!shouldPrompt({ ...p, shown: promptShown() })) return false;
  markPromptShown();
  track(HOME_SCREEN_PROMPT, homeScreenPrompt('shown', 'auto'));
  return true;
}

/** Человек выбрал «Добавить» (в листе или строкой меню): событие и штатное окно Telegram. */
export function acceptHomeScreen(source: HomeScreenPromptSource): void {
  track(HOME_SCREEN_PROMPT, homeScreenPrompt('accepted', source));
  addToHomeScreen();
}
/** Лист закрыт без добавления (фон, «Не сейчас», системная «Назад»). */
export function dismissHomeScreen(source: HomeScreenPromptSource): void {
  track(HOME_SCREEN_PROMPT, homeScreenPrompt('dismissed', source));
}
